"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.countPostChars = countPostChars;
exports.linksIn = linksIn;
exports.linkSpansIn = linkSpansIn;
exports.emojiIn = emojiIn;
const enums_1 = require("./enums");
const tlds_1 = require("./tlds");
/**
 * How long a post's text is by each platform's OWN rule, for the character limits.
 *
 * ONE function behind the on-screen counters, the composer's Save / Schedule / Publish gates and the
 * server's publish check, so they cannot disagree about whether a post fits.
 *
 * - X counts as its own library does (twitter-text 3.1.0, configuration v3): text is normalised to
 *   NFC; every link counts 23 whatever its length; an emoji on X's emoji list counts 2 however many
 *   code points it is made of; every other code point counts 1 in U+0000-U+10FF, U+2000-U+200D,
 *   U+2010-U+201F and U+2032-U+2037, and 2 everywhere else (which is what makes Chinese, Japanese
 *   and Korean count double).
 *   X's emoji list is the one of 2018 (Emoji 11.0). A newer emoji, or any sequence the list does
 *   not have, counts as its parts, as on X: a person at a laptop is 5, not 2. If X one day counts
 *   such a sequence as one emoji, this count is higher than X's, not lower. The list is written
 *   below as code point ranges, found by trying candidate sequences against the emoji pattern
 *   twitter-text 3.1.0 uses: measured data, not its code.
 *   A text longer than 49,432 UTF-16 units after NFC is not scanned: no post that fits on X is
 *   that long, so it counts as its length - over the limit, though it can be less than X's own
 *   number. A text over four times that length, or with a long run of combining marks that NFC
 *   re-sorts (more than 300 in a row, see hasLongMarkRun; in a time that grows with the square of
 *   their number), is answered the same way before NFC: X refuses it too, except in a link's path.
 *   Up to that length the tests pin the same count as twitter-text 3.1.0 on every X case in
 *   char-count.test.ts, and the same was checked against the library on 900,000 generated texts
 *   (words, links, emoji, many scripts, links that end inside an emoji): never under X's count on
 *   those. The tests also time 15 hostile shapes of text (long unbroken runs, many places a link
 *   could start, many links, runs of combining marks) at that length and at a million characters:
 *   each must answer in under a second.
 * - Threads counts each emoji as its number of UTF-8 bytes (Meta, Threads API "Posts" >
 *   Limitations: "Emojis are counted as the number of UTF-8 bytes"); everything else counts as it
 *   always has here. Links are not special. Where the runtime does not know the emoji properties
 *   (an older browser), every code point counts its UTF-8 bytes instead: never under, higher for
 *   text that is not ASCII.
 * - Every other platform: `text.length`, unchanged.
 *
 * An under-count is the costly mistake: it lets a post be approved and scheduled and then fail at
 * the platform, while an over-count only blocks a post the platform would have taken (visible, and
 * fixed by shortening it).
 */
function countPostChars(platform, text) {
    if (platform === enums_1.SocialPlatform.X)
        return countForX(text);
    if (platform === enums_1.SocialPlatform.THREADS)
        return countForThreads(text);
    return text.length;
}
/**
 * The links in `text` that X counts as 23 characters each, in order: exactly the ones
 * countPostChars(X, text) charges 23 for, as they stand in the NFC form of `text`. So a bare
 * "example.com" is one, a full stop or a bracket after a link is not part of it, and a t.co link
 * ends at its slug. For checks that a text keeps its links exactly as written.
 *
 * A text the X count does not read has none: one longer than any post X can take, or one with a
 * long run of combining marks (see countPostChars). It is over X's limit whatever it holds, and
 * reading it could hold the caller for seconds.
 */
function linksIn(text) {
    const read = countedLinks(text);
    return read ? read.links.map((link) => read.normalized.slice(link.start, link.end)) : [];
}
/**
 * Where the links {@link linksIn} returns stand: each one's start and end (end excluded) in the NFC
 * form of `text`, in order. That is where X reads it, which a search for its characters can miss:
 * in "Write to hello@example.com or visit example.com" the link is the second "example.com" (X
 * reads no link inside an e-mail address). The same reading as linksIn, so a text the X count does
 * not read has none.
 */
function linkSpansIn(text) {
    return countedLinks(text)?.links ?? [];
}
/** The links countForX reads in `text`, with the NFC form they stand in; null for a text it does not read. */
function countedLinks(text) {
    // countForX's two checks, in its order: the first comes before NFC, which re-sorts a run of
    // combining marks in a time that grows with the square of its length.
    if (text.length > 4 * X_LONGEST_TEXT_THAT_CAN_FIT || hasLongMarkRun(text))
        return null;
    const normalized = text.normalize('NFC');
    if (normalized.length > X_LONGEST_TEXT_THAT_CAN_FIT)
        return null;
    const links = [];
    let at = 0;
    for (const link of xLinks(normalized)) {
        // As countForX: a link that starts inside one already counted is skipped.
        if (link.start < at)
            continue;
        links.push(link);
        at = link.end;
    }
    return { normalized, links };
}
/**
 * The emoji in `text`, in order, each one whole (a family, a flag, a keycap or an emoji with a skin
 * tone is one): every grapheme the Threads count treats as an emoji - a pictograph, a flag (two
 * regional indicators) or a keycap. Where the runtime has no Intl.Segmenter or does not know the
 * emoji properties (an older browser; Node has both), it finds none.
 */
function emojiIn(text) {
    const graphemes = graphemeSegmenter();
    if (!graphemes || !EXTENDED_PICTOGRAPHIC)
        return [];
    const found = [];
    for (const { segment } of graphemes.segment(text)) {
        if (isEmoji(segment, EXTENDED_PICTOGRAPHIC))
            found.push(segment);
    }
    return found;
}
let segmenterFor;
let segmenter = null;
/** The runtime's grapheme splitter, or null where `Intl.Segmenter` does not exist. */
function graphemeSegmenter() {
    const Segmenter = Intl.Segmenter;
    if (Segmenter !== segmenterFor) {
        segmenterFor = Segmenter;
        segmenter = typeof Segmenter === 'function' ? new Segmenter(undefined, { granularity: 'grapheme' }) : null;
    }
    return segmenter;
}
/**
 * A `u` pattern built when this module loads, or null where the runtime does not know a property
 * it names. Written as a literal, such a pattern would stop a browser that does not know the name
 * from parsing the whole bundle, and the app would not start at all.
 */
function unicodePattern(source) {
    try {
        return new RegExp(source, 'u');
    }
    catch {
        return null;
    }
}
const EXTENDED_PICTOGRAPHIC = unicodePattern('\\p{Extended_Pictographic}');
const REGIONAL_INDICATOR_PAIR = /[\u{1F1E6}-\u{1F1FF}]{2}/u;
const COMBINING_KEYCAP = '⃣';
/** A code point that can be part of an emoji: what the no-Segmenter fallback treats as one. */
const EMOJI_PART = unicodePattern('[\\p{Extended_Pictographic}\\p{Emoji_Component}]');
/** Whether one grapheme is an emoji: a pictograph, a flag (two regional indicators) or a keycap. */
function isEmoji(grapheme, pictographic) {
    return (pictographic.test(grapheme) ||
        REGIONAL_INDICATOR_PAIR.test(grapheme) ||
        grapheme.includes(COMBINING_KEYCAP));
}
/** UTF-8 length of `text` (a lone surrogate counts 3, as an encoder writes U+FFFD). */
function utf8Bytes(text) {
    let bytes = 0;
    for (const ch of text) {
        const cp = ch.codePointAt(0);
        bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
}
// ── Threads ────────────────────────────────────────────────────────────────
function countForThreads(text) {
    // A runtime that does not know the emoji properties: every code point by its UTF-8 bytes. That is
    // never under Threads' count (no code point has fewer UTF-8 bytes than UTF-16 units), only higher
    // for text that is not ASCII.
    if (!EXTENDED_PICTOGRAPHIC || !EMOJI_PART)
        return utf8Bytes(text);
    const graphemes = graphemeSegmenter();
    let count = 0;
    if (!graphemes) {
        // No grapheme splitter: every code point that can belong to an emoji counts its own bytes.
        for (const ch of text)
            count += EMOJI_PART.test(ch) ? utf8Bytes(ch) : ch.length;
        return count;
    }
    for (const { segment } of graphemes.segment(text)) {
        count += isEmoji(segment, EXTENDED_PICTOGRAPHIC) ? utf8Bytes(segment) : segment.length;
    }
    return count;
}
// ── X ──────────────────────────────────────────────────────────────────────
/** What every link counts on X, whatever its length (v3 `transformedURLLength`). */
const X_LINK_LENGTH = 23;
/** What one emoji counts on X, however many code points it is made of (v3 `defaultWeight` / `scale`). */
const X_EMOJI_LENGTH = 2;
/** The code points X counts as ONE (v3 `ranges`, weight 100 of scale 100); all others count two. */
const X_SINGLE_WEIGHT_RANGES = [
    [0x0000, 0x10ff],
    [0x2000, 0x200d],
    [0x2010, 0x201f],
    [0x2032, 0x2037],
];
function xCodePointWeight(cp) {
    return X_SINGLE_WEIGHT_RANGES.some(([from, to]) => cp >= from && cp <= to) ? 1 : 2;
}
function countForX(text) {
    // NFC re-sorts a run of combining marks in a time that grows with the square of its length, and
    // shrinks a text at most four times: these are over the limit, answered unread.
    if (text.length > 4 * X_LONGEST_TEXT_THAT_CAN_FIT || hasLongMarkRun(text))
        return text.length;
    const normalized = text.normalize('NFC');
    // Longer than any post X can take: over the limit whatever it holds, so it is not read.
    if (normalized.length > X_LONGEST_TEXT_THAT_CAN_FIT)
        return normalized.length;
    const emoji = xEmojiStarts(normalized);
    let count = 0;
    let at = 0;
    for (const link of xLinks(normalized)) {
        // X skips a link that starts inside one it has already counted.
        if (link.start < at)
            continue;
        count += xTextWeight(normalized, emoji, at, link.start) + X_LINK_LENGTH;
        at = link.end;
    }
    return count + xTextWeight(normalized, emoji, at, normalized.length);
}
/** The emoji X counts as one, found as X finds them: in one scan of the whole text, left to right.
 *  Each match's length is kept at its start. */
function xEmojiStarts(text) {
    const lengths = new Uint8Array(text.length);
    let at = 0;
    while (at < text.length) {
        const length = xEmojiLength(text, at);
        if (length > 0) {
            lengths[at] = length;
            at += length;
        }
        else {
            at += text.codePointAt(at) > 0xffff ? 2 : 1;
        }
    }
    return lengths;
}
/**
 * Whether `text` holds a run of more than 300 combining marks in which more than 300 in a row are
 * marks NFC re-sorts: of a combining class other than 0, counted as NFC decomposes them (U+0344 is
 * two). Outside a link's path such a run cannot fit on X: those marks never join one another, at
 * most 3 join the letter before them, and each weighs at least 1. A mark of class 0 ends the row:
 * vowel signs NFC never re-sorts but may join in pairs or threes (Tamil U+0BC6 U+0BBE is U+0BCA).
 */
function hasLongMarkRun(text) {
    const runs = /\p{M}+/gu;
    for (let run = runs.exec(text); run; run = runs.exec(text)) {
        // A shorter run is read: NFC re-sorts it quickly, and it may fit.
        if (run[0].length <= 300)
            continue;
        let inARow = 0;
        for (const mark of run[0]) {
            const { continuesRow, resorted } = nfcResorts(mark);
            inARow = continuesRow ? inARow + resorted : resorted;
            if (inARow > 300)
                return true;
        }
    }
    return false;
}
const NFC_RESORTS = new Map();
/** How NFC decomposes `mark`: whether all of it is marks NFC re-sorts, and how many of those it
 *  ends with. Such a mark moves past U+0334 (class 1) or U+0345 (class 240); one of class 0 moves
 *  past neither. Worked out the first time a mark is met, then kept. */
function nfcResorts(mark) {
    let known = NFC_RESORTS.get(mark);
    if (!known) {
        const resorts = (part) => (part + '\u0334').normalize('NFD') !== `${part}\u0334` ||
            ('\u0345' + part).normalize('NFD') !== `\u0345${part}`;
        const parts = [...mark.normalize('NFD')];
        let resorted = 0;
        while (resorted < parts.length && resorts(parts[parts.length - 1 - resorted]))
            resorted += 1;
        known = { continuesRow: resorted === parts.length, resorted };
        NFC_RESORTS.set(mark, known);
    }
    return known;
}
/** X's weight of text[from, to), which holds no link: 2 per emoji X counts as one, and each other
 *  code point by its own weight. */
function xTextWeight(text, emojiStarts, from, to) {
    let count = 0;
    let at = from;
    while (at < to) {
        const emoji = emojiStarts[at];
        if (emoji > 0 && at + emoji <= to) {
            count += X_EMOJI_LENGTH;
            at += emoji;
        }
        else {
            const cp = text.codePointAt(at);
            count += xCodePointWeight(cp);
            at += cp > 0xffff ? 2 : 1;
        }
    }
    return count;
}
// ── X emoji ────────────────────────────────────────────────────────────────
//
// X counts a sequence of code points as ONE emoji only when its emoji list has it (twitter-text
// 3.1.0 takes it from twemoji-parser 11: the emoji of 2018, Emoji 11.0). Anything else counts as
// its parts - each emoji 2, a joiner 1, a mark or a letter by its own weight - however a grapheme
// splitter would group them: a newer emoji such as a person at a laptop (5), a red heart with the
// text-style selector (4), a smiley with combining accents glued to it, an unknown flag (4). A
// single code point needs no entry: it weighs the same either way. The lists below are those
// sequences, as code point ranges (hex), checked one by one against twitter-text 3.1.0.
/** Emoji that stay one emoji with U+FE0F (emoji style) after them. */
const X_TAKES_VS16 = 'a9 ae 203c 2049 2122 2139 2194-2199 21a9-21aa 231a-231b 2328 23cf 23ed-23ef 23f1-23f2 ' +
    '23f8-23fa 24c2 25aa-25ab 25b6 25c0 25fb-25fe 2600-2604 260e 2611 2614-2615 2618 261d 2620 ' +
    '2622-2623 2626 262a 262e-262f 2638-263a 2640 2642 2648-2653 265f-2660 2663 2665-2666 2668 ' +
    '267b 267f 2692-2697 2699 269b-269c 26a0-26a1 26aa-26ab 26b0-26b1 26bd-26be 26c4-26c5 26c8 ' +
    '26cf 26d1 26d3-26d4 26e9-26ea 26f0-26f5 26f7-26fa 26fd 2702 2708-2709 270c-270d 270f 2712 ' +
    '2714 2716 271d 2721 2733-2734 2744 2747 2757 2763-2764 27a1 2934-2935 2b05-2b07 2b1b-2b1c ' +
    '2b50 2b55 3030 303d 3297 3299 1f004 1f170-1f171 1f17e-1f17f 1f202 1f21a 1f22f 1f237 1f321 ' +
    '1f324-1f32c 1f336 1f37d 1f396-1f397 1f399-1f39b 1f39e-1f39f 1f3cb-1f3ce 1f3d4-1f3df 1f3f3 ' +
    '1f3f5 1f3f7 1f43f 1f441 1f4fd 1f549-1f54a 1f56f-1f570 1f573-1f579 1f587 1f58a-1f58d 1f590 ' +
    '1f5a5 1f5a8 1f5b1-1f5b2 1f5bc 1f5c2-1f5c4 1f5d1-1f5d3 1f5dc-1f5de 1f5e1 1f5e3 1f5e8 1f5ef ' +
    '1f5f3 1f5fa 1f6cb 1f6cd-1f6cf 1f6e0-1f6e5 1f6e9 1f6f0 1f6f3';
/** Emoji that stay one emoji with a skin tone after them... */
const X_TAKES_TONE = '261d 26f7 26f9 270a-270d 1f385 1f3c2-1f3c4 1f3c7 1f3ca-1f3cc 1f442-1f443 1f446-1f450 ' +
    '1f466-1f469 1f46e 1f470-1f478 1f47c 1f481-1f483 1f485-1f487 1f4aa 1f574-1f575 1f57a 1f590 ' +
    '1f595-1f596 1f645-1f647 1f64b-1f64f 1f6a3 1f6b4-1f6b6 1f6c0 1f6cc 1f918-1f91c 1f91e-1f91f ' +
    '1f926 1f930-1f939 1f93d-1f93e 1f9b5-1f9b6 1f9b8-1f9b9 1f9d1-1f9dd';
/** ...of which these also take U+FE0F before the tone. */
const X_TAKES_VS16_AND_TONE = '261d 26f7 26f9 270c-270d 1f3cb-1f3cc 1f574-1f575 1f590';
/** People who are one emoji with "joiner, female or male sign, U+FE0F" after them, with or without
 *  a skin tone... */
const X_GENDERED = '1f3c3-1f3c4 1f3ca 1f46e 1f471 1f473 1f477 1f481-1f482 1f486-1f487 1f645-1f647 1f64b ' +
    '1f64d-1f64e 1f6a3 1f6b4-1f6b6 1f926 1f935 1f937-1f939 1f93d-1f93e 1f9b8-1f9b9 1f9d6-1f9dd';
/** ...only after a skin tone or U+FE0F... */
const X_GENDERED_AFTER_TONE_OR_VS16 = '26f9 1f3cb-1f3cc 1f574-1f575';
/** ...only without a skin tone. */
const X_GENDERED_WITHOUT_TONE = '1f46f 1f93c 1f9de-1f9df';
/** What a man or a woman (and an optional skin tone) joins to make a profession or a hair
 *  style... */
const X_PROFESSIONS = '1f33e 1f373 1f393 1f3a4 1f3a8 1f3eb 1f3ed 1f4bb-1f4bc 1f527 1f52c 1f680 1f692 1f9b0-1f9b3';
/** ...and the three that need U+FE0F after them. */
const X_PROFESSIONS_WITH_VS16 = '2695-2696 2708';
/** The country and region flags X knows, by first letter: "A:CDE" is AC, AD and AE. */
const X_REGION_FLAGS = 'A:CDEFGILMOQRSTUWXZ B:ABDEFGHIJLMNOQRSTVWYZ C:ACDFGHIKLMNOPRUVWXYZ D:EGJKMOZ E:ACEGHRSTU ' +
    'F:IJKMOR G:ABDEFGHILMNPQRSTUWY H:KMNRTU I:CDELMNOQRST J:EMOP K:EGHIMNPRWYZ L:ABCIKRSTUVY ' +
    'M:ACDEFGHKLMNOPQRSTUVWXYZ N:ACEFGILOPRUZ O:M P:AEFGHKLMNRSTWY Q:A R:EOSUW ' +
    'S:ABCDEGHIJKLMNORSTVXYZ T:ACDFGHJKLMNORTVWZ U:AGMNSYZ V:ACEGINU W:FS X:K Y:ET Z:AMW';
/** A regex character class body (`u` flag) for code point ranges written "a9 2194-2199 ...". */
function codePointClass(ranges) {
    return ranges
        .split(' ')
        .map((range) => range.split('-').map((hex) => `\\u{${hex}}`).join('-'))
        .join('');
}
/** The regional indicator symbol for the letter A-Z. */
function regionalIndicator(letter) {
    return `\\u{${(0x1f1e6 + letter.charCodeAt(0) - 65).toString(16)}}`;
}
const TONE = '[\\u{1f3fb}-\\u{1f3ff}]';
const ZWJ = '\\u{200d}';
const VS16 = '\\u{fe0f}';
const MAN = '\\u{1f468}';
const WOMAN = '\\u{1f469}';
const GIRL = '\\u{1f467}';
const BOY = '\\u{1f466}';
const HEART = `\\u{2764}${VS16}`;
const KISS = '\\u{1f48b}';
/**
 * Every sequence of more than one code point (but a keycap, see xEmojiLength) that X counts as one
 * emoji. Where two start alike the longer comes first: X takes the first that fits.
 */
const X_EMOJI = new RegExp([
    // a profession or a hair style
    `[${MAN}${WOMAN}]${TONE}?${ZWJ}` +
        `(?:[${codePointClass(X_PROFESSIONS_WITH_VS16)}]${VS16}|[${codePointClass(X_PROFESSIONS)}])`,
    // a family: one or two parents (no woman before a man), then one or two children (no boy
    // before a girl)
    `(?:${MAN}${ZWJ}[${MAN}${WOMAN}]|${WOMAN}${ZWJ}${WOMAN}|[${MAN}${WOMAN}])${ZWJ}` +
        `(?:${GIRL}${ZWJ}[${BOY}${GIRL}]|${BOY}${ZWJ}${BOY}|[${BOY}${GIRL}])`,
    // a couple with a heart, kissing or not (no man before a woman)
    `${WOMAN}${ZWJ}${HEART}${ZWJ}(?:${KISS}${ZWJ})?[${MAN}${WOMAN}]`,
    `${MAN}${ZWJ}${HEART}${ZWJ}(?:${KISS}${ZWJ})?${MAN}`,
    // a woman or a man doing something
    `(?:[${codePointClass(`${X_GENDERED} ${X_GENDERED_AFTER_TONE_OR_VS16}`)}]${TONE}` +
        `|[${codePointClass(X_GENDERED_AFTER_TONE_OR_VS16)}]${VS16}` +
        `|[${codePointClass(`${X_GENDERED} ${X_GENDERED_WITHOUT_TONE}`)}])` +
        `${ZWJ}[\\u{2640}\\u{2642}]${VS16}`,
    // the rainbow flag, the pirate flag, the eye in a speech bubble
    `\\u{1f3f3}${VS16}${ZWJ}\\u{1f308}`,
    `\\u{1f3f4}${ZWJ}\\u{2620}${VS16}`,
    `\\u{1f441}${ZWJ}\\u{1f5e8}`,
    // England, Scotland, Wales
    '\\u{1f3f4}\\u{e0067}\\u{e0062}' +
        '(?:\\u{e0065}\\u{e006e}\\u{e0067}' +
        '|\\u{e0073}\\u{e0063}\\u{e0074}' +
        '|\\u{e0077}\\u{e006c}\\u{e0073})' +
        '\\u{e007f}',
    // with a skin tone, or U+FE0F
    `[${codePointClass(X_TAKES_VS16_AND_TONE)}]${VS16}${TONE}`,
    `[${codePointClass(X_TAKES_TONE)}]${TONE}`,
    `[${codePointClass(X_TAKES_VS16)}]${VS16}`,
    // a country or region flag
    ...X_REGION_FLAGS.split(' ').map((group) => {
        const [first, seconds] = group.split(':');
        return `${regionalIndicator(first)}[${[...seconds].map(regionalIndicator).join('')}]`;
    }),
].join('|'), 'uy');
/** UTF-16 length of the emoji X counts as one that starts at `at`, or 0. */
function xEmojiLength(text, at) {
    const unit = text.charCodeAt(at);
    if (unit === 0x23 || unit === 0x2a || (unit >= 0x30 && unit <= 0x39)) {
        // A keycap: "#", "*" or a digit, an optional U+FE0F, then U+20E3.
        const mark = text.charCodeAt(at + 1) === 0xfe0f ? at + 2 : at + 1;
        return text.charCodeAt(mark) === 0x20e3 ? mark + 1 - at : 0;
    }
    // Every other one starts with (c), (r), a character in U+203C-U+3299 or one beyond U+FFFF.
    const mayStart = unit === 0xa9 ||
        unit === 0xae ||
        (unit >= 0x203c && unit <= 0x3299) ||
        (unit >= 0xd83c && unit <= 0xd83e);
    if (!mayStart)
        return 0;
    X_EMOJI.lastIndex = at;
    return X_EMOJI.test(text) ? X_EMOJI.lastIndex - at : 0;
}
// ── X links (twitter-text's extractUrlsWithIndices, in one pass) ─────────────
const MAX_URL_LENGTH = 4096;
const MAX_LABEL_LENGTH = 63;
const MAX_TCO_SLUG = 40;
/**
 * No text longer than this fits on X. A link counts 23, so at most 12 fit in 280, and none covers
 * more than MAX_URL_LENGTH UTF-16 units. The rest weighs at least 1 per 7 units: each code point at
 * least 1 per unit, an emoji X counts as one 2 for at most 14 units (the flags of England, Scotland
 * and Wales). So a text with L links that fits is at most L x MAX_URL_LENGTH + 7 x (280 - 23 x L)
 * units long - 12 x MAX_URL_LENGTH + 28 at the most, under this ceiling.
 */
const X_LONGEST_TEXT_THAT_CAN_FIT = 12 * MAX_URL_LENGTH + 280;
const TOP_LEVEL_DOMAINS = new Set([...tlds_1.GENERIC_TLDS, ...tlds_1.COUNTRY_TLDS].map((tld) => tld.toLowerCase()));
const LONGEST_TOP_LEVEL_DOMAIN = Math.max(...[...TOP_LEVEL_DOMAINS].map((tld) => tld.length));
/** The TLDs that are not all a-z, longest first ("嘉里大酒店" before "嘉里", as X has them). */
const OTHER_TOP_LEVEL_DOMAIN = new RegExp(`(?:${[...TOP_LEVEL_DOMAINS]
    .filter((tld) => !/^[a-z]+$/.test(tld))
    .sort((a, b) => b.length - a.length)
    .join('|')})(?![0-9a-z@+-])`, 'iy');
const PUNYCODE_TOP_LEVEL_DOMAIN = /xn--[0-9a-z-]+/iy;
/** What a TLD may not be followed by: a letter, a digit, @, + or -. */
const AFTER_TLD = /[0-9a-z@+-]/i;
const SCHEME = /https?:\/\//iy;
const TCO_LINK = /^https?:\/\/t\.co\/([a-z0-9]+)(?:\?[a-z0-9!?*'@();:&=+$\/%#[\]\-_.,~|]*[a-z0-9\-_&=#\/])?/i;
/** twitter-text's `latinAccentChars`: letters it accepts in a domain or a path besides a-z. */
const LATIN_ACCENTS = '\\u00c0-\\u00d6\\u00d8-\\u00f6\\u00f8-\\u00ff\\u0100-\\u024f\\u0253\\u0254\\u0256\\u0257\\u0259' +
    '\\u025b\\u0263\\u0268\\u026f\\u0272\\u0289\\u028b\\u02bb\\u0300-\\u036f\\u1e00-\\u1eff';
const CYRILLIC = '\\u0400-\\u04ff';
/** What may not be in a domain label: ASCII punctuation (but " and `), a space, a direction mark,
 *  U+FFFE, U+FEFF or U+FFFF. */
const NOT_IN_DOMAIN = '!#-\\/:-@\\[-^_{-~\\t-\\r \\u0085\\u00a0\\u1680\\u180e\\u2000-\\u200a\\u2028\\u2029\\u202f' +
    '\\u205f\\u3000\\u202a-\\u202e\\u061c\\u200e\\u200f\\u2066-\\u2069\\ufffe\\ufeff\\uffff';
// What each UTF-16 unit may be in a link, tested one unit at a time and in either case, as X's
// patterns test it. Worked out the first time a unit is met, then kept.
const DOMAIN_CHAR = 1;
const PRECEDES_LINK = 2;
const ASCII_DOMAIN_CHAR = 4;
const PATH_CHAR = 8;
const PATH_END_CHAR = 16;
const QUERY_CHAR = 32;
const QUERY_END_CHAR = 64;
const KNOWN = 128;
const UNIT_TESTS = [
    [DOMAIN_CHAR, new RegExp(`[^${NOT_IN_DOMAIN}]`)],
    // anything but a letter, a digit, @, $, # (or a full-width @ / #) and U+FFFE, U+FEFF, U+FFFF
    [PRECEDES_LINK, new RegExp('[^a-z0-9@\\uff20$#\\uff03\\ufffe\\ufeff\\uffff]', 'i')],
    [ASCII_DOMAIN_CHAR, new RegExp(`[\\-a-z0-9${LATIN_ACCENTS}]`, 'i')],
    [
        PATH_CHAR,
        new RegExp(`[a-z${CYRILLIC}0-9!*';:=+,.$\\/%#\\[\\]\\-\\u2013_~@|&${LATIN_ACCENTS}]`, 'i'),
    ],
    [PATH_END_CHAR, new RegExp(`[+\\-a-z${CYRILLIC}0-9=_#\\/${LATIN_ACCENTS}]`, 'i')],
    [QUERY_CHAR, /[a-z0-9!?*'@();:&=+$\/%#[\]\-_.,~|]/i],
    [QUERY_END_CHAR, /[a-z0-9\-_&=#\/]/i],
];
const unitClasses = new Uint8Array(0x10000);
function unitIs(text, at, what) {
    if (at >= text.length)
        return false;
    const unit = text.charCodeAt(at);
    let classes = unitClasses[unit];
    if (classes === 0) {
        const char = String.fromCharCode(unit);
        classes = KNOWN;
        for (const [bit, test] of UNIT_TESTS)
            if (test.test(char))
                classes |= bit;
        unitClasses[unit] = classes;
    }
    return (classes & what) !== 0;
}
/** A character of a domain label: a domain character, or "-" / "_" between two. */
function inLabel(text, at) {
    const unit = text.charCodeAt(at);
    return unit === 0x2d || unit === 0x5f || unitIs(text, at, DOMAIN_CHAR);
}
function isAsciiLetter(unit) {
    return (unit | 0x20) >= 0x61 && (unit | 0x20) <= 0x7a;
}
function isDigit(text, at) {
    const unit = text.charCodeAt(at);
    return unit >= 0x30 && unit <= 0x39;
}
/** Where the TLD X reads at `at` ends (the domain may end where `text` does), or -1. */
function tldEnd(text, at) {
    let end = at;
    while (isAsciiLetter(text.charCodeAt(end)))
        end += 1;
    if (end > at &&
        end - at <= LONGEST_TOP_LEVEL_DOMAIN &&
        !AFTER_TLD.test(text[end] ?? '') &&
        TOP_LEVEL_DOMAINS.has(text.slice(at, end).toLowerCase())) {
        return end;
    }
    OTHER_TOP_LEVEL_DOMAIN.lastIndex = at;
    if (OTHER_TOP_LEVEL_DOMAIN.test(text))
        return OTHER_TOP_LEVEL_DOMAIN.lastIndex;
    PUNYCODE_TOP_LEVEL_DOMAIN.lastIndex = at;
    return PUNYCODE_TOP_LEVEL_DOMAIN.test(text) ? PUNYCODE_TOP_LEVEL_DOMAIN.lastIndex : -1;
}
/**
 * For every position, where the domain X reads from there ends, or -1: labels each followed by "."
 * (as many as it can take; no "_" in the last one), then a TLD. Worked out in one pass from the
 * end, so a place where a link could start costs a look-up, not a read of everything after it.
 */
function domainEnds(text) {
    const ends = new Int32Array(text.length + 1).fill(-1);
    let last = text.length - 1;
    while (last >= 0) {
        if (!inLabel(text, last)) {
            last -= 1;
            continue;
        }
        let first = last;
        while (first > 0 && inLabel(text, first - 1))
            first -= 1;
        const dot = last + 1;
        if (text[dot] === '.' && unitIs(text, last, DOMAIN_CHAR)) {
            const longer = ends[dot + 1];
            let tld;
            let underscore = false;
            for (let at = last; at >= first; at -= 1) {
                if (text[at] === '_')
                    underscore = true;
                else if (unitIs(text, at, DOMAIN_CHAR)) {
                    if (longer >= 0)
                        ends[at] = longer;
                    else if (!underscore)
                        ends[at] = tld ??= tldEnd(text, dot + 1);
                }
            }
        }
        last = first - 1;
    }
    return ends;
}
function readLink(text, domains, start, before) {
    SCHEME.lastIndex = start;
    const hasScheme = (text.charCodeAt(start) | 0x20) === 0x68 && SCHEME.test(text);
    let domain = hasScheme ? SCHEME.lastIndex : start;
    let domainEnd = domains[domain];
    if (domainEnd < 0 && domain > start) {
        domain = start;
        domainEnd = domains[start];
    }
    if (domainEnd < 0)
        return null;
    let end = domainEnd;
    if (text[end] === ':' && isDigit(text, end + 1)) {
        end += 2;
        while (isDigit(text, end))
            end += 1;
    }
    const hasPath = text[end] === '/';
    if (hasPath)
        end = pathEnd(text, end);
    if (text[end] === '?')
        end = queryEnd(text, end);
    return { start, domain, domainEnd, end, hasPath, before };
}
/** The end of the path characters from `at`. */
function pathRun(text, at) {
    let end = at;
    while (unitIs(text, end, PATH_CHAR))
        end += 1;
    return end;
}
/** End of the bracketed group X allows in a path at `open` - "(x)" or "(x(y)z)" - or -1. */
function bracketsEnd(text, open) {
    const inside = pathRun(text, open + 1);
    if (text[inside] === ')')
        return inside > open + 1 ? inside + 1 : -1;
    if (text[inside] !== '(')
        return -1;
    const nested = pathRun(text, inside + 1);
    if (nested === inside + 1 || text[nested] !== ')')
        return -1;
    const close = pathRun(text, nested + 1);
    return text[close] === ')' ? close + 1 : -1;
}
/** End of the path X takes after the "/" at `slash`: path characters and bracketed groups, up to
 *  the last character a path may end with (or just the "/"). */
function pathEnd(text, slash) {
    let end = slash + 1;
    let at = slash + 1;
    while (at < text.length) {
        if (unitIs(text, at, PATH_CHAR)) {
            at += 1;
            if (unitIs(text, at - 1, PATH_END_CHAR))
                end = at;
        }
        else {
            const group = text[at] === '(' ? bracketsEnd(text, at) : -1;
            if (group < 0)
                break;
            // A group ends the path too, but only one that follows such a character, the "/" or another
            // such group: "/x(a)" keeps "(a)", "/x.(a)" does not.
            if (at === end)
                end = group;
            at = group;
        }
    }
    return end;
}
/** End of the query X takes after the "?" at `question`, up to the last character a query may end
 *  with - or `question` itself if there is none. */
function queryEnd(text, question) {
    let end = question;
    for (let at = question + 1; unitIs(text, at, QUERY_CHAR); at += 1) {
        if (unitIs(text, at, QUERY_END_CHAR))
            end = at + 1;
    }
    return end;
}
/**
 * The ASCII domains X finds in `domain` (labels of a-z, 0-9, "-" and Latin accents, each followed
 * by ".", then a TLD) as [offset, length]. Like X, it places each by searching for its text from
 * the end of the previous one.
 */
function asciiDomains(domain) {
    // The usual case, a domain of a-z, 0-9, "-" and "." only, is one ASCII domain.
    if (/^[a-z0-9.-]+$/i.test(domain))
        return [[0, domain.length]];
    const ends = new Int32Array(domain.length + 1).fill(-1);
    let last = domain.length - 1;
    while (last >= 0) {
        if (!unitIs(domain, last, ASCII_DOMAIN_CHAR)) {
            last -= 1;
            continue;
        }
        let first = last;
        while (first > 0 && unitIs(domain, first - 1, ASCII_DOMAIN_CHAR))
            first -= 1;
        if (domain[last + 1] === '.') {
            const longer = ends[last + 2];
            const end = longer >= 0 ? longer : tldEnd(domain, last + 2);
            if (end >= 0)
                ends.fill(end, first, last + 1);
        }
        last = first - 1;
    }
    const found = [];
    let placed = 0;
    for (let at = 0; at < domain.length; at += 1) {
        if (ends[at] < 0)
            continue;
        const ascii = domain.slice(at, ends[at]);
        const offset = domain.indexOf(ascii, placed);
        found.push([offset, ascii.length]);
        placed = offset + ascii.length;
        at = ends[at] - 1;
    }
    return found;
}
/** twitter-text's IDNA test: every label 1-63 characters in punycode; a domain that starts "xn--"
 *  must hold an ASCII domain. */
function fitsDns(domain) {
    if (domain.startsWith('xn--') && asciiDomains(domain).length === 0)
        return false;
    // ASCII stays as it is in punycode.
    if (!/[^\0-\x7f]/.test(domain)) {
        return domain.split('.').every((label) => label.length <= MAX_LABEL_LENGTH);
    }
    return domain.split('.').every((label) => {
        const length = punycodeLength(label);
        return length >= 1 && length <= MAX_LABEL_LENGTH;
    });
}
const PUNYCODE_SEPARATORS = new RegExp('[\\u3002\\uff0e\\uff61]');
/** Length of `label` in punycode, as the punycode library twitter-text uses writes it. */
function punycodeLength(label) {
    return label.split(PUNYCODE_SEPARATORS).reduce((sum, part, i) => {
        const encoded = /[^\0-\x7f]/.test(part) ? 'xn--'.length + punycodeDigits(part) : part.length;
        return sum + (i > 0 ? 1 : 0) + encoded;
    }, 0);
}
/** RFC 3492's encoding of `part`, counted rather than written: its ASCII, "-", then the deltas. */
function punycodeDigits(part) {
    const points = [];
    for (let i = 0; i < part.length; i += 1) {
        const unit = part.charCodeAt(i);
        const next = part.charCodeAt(i + 1);
        if (unit >= 0xd800 && unit <= 0xdbff && next >= 0xdc00 && next <= 0xdfff) {
            points.push(((unit - 0xd800) << 10) + (next - 0xdc00) + 0x10000);
            i += 1;
        }
        else {
            points.push(unit);
        }
    }
    const basic = points.filter((point) => point < 0x80).length;
    // Every code point writes at least one character: past 63 there is nothing to work out.
    if (points.length + (basic > 0 ? 1 : 0) > MAX_LABEL_LENGTH)
        return MAX_LABEL_LENGTH + 1;
    let length = basic + (basic > 0 ? 1 : 0);
    let n = 0x80;
    let delta = 0;
    let bias = 72;
    let handled = basic;
    while (handled < points.length) {
        const m = Math.min(...points.filter((point) => point >= n));
        const handledPlusOne = handled + 1;
        delta += (m - n) * handledPlusOne;
        n = m;
        for (const point of points) {
            if (point < n)
                delta += 1;
            if (point === n) {
                let q = delta;
                for (let k = 36;; k += 36) {
                    const t = k <= bias ? 1 : k >= bias + 26 ? 26 : k - bias;
                    if (q < t)
                        break;
                    length += 1;
                    q = Math.floor((q - t) / (36 - t));
                }
                length += 1;
                bias = punycodeBias(delta, handledPlusOne, handled === basic);
                delta = 0;
                handled += 1;
            }
        }
        delta += 1;
        n += 1;
    }
    return length;
}
function punycodeBias(delta, points, first) {
    let d = first ? Math.floor(delta / 700) : delta >> 1;
    d += Math.floor(d / points);
    let k = 0;
    while (d > 455) {
        d = Math.floor(d / 35);
        k += 36;
    }
    return Math.floor(k + (36 * d) / (d + 38));
}
/** What X links in a match: nothing, all of it, a t.co link up to its slug, or - with no scheme -
 *  the ASCII domains in its domain, the last one with the path if there is one. */
function linksInMatch(text, match, links) {
    const scheme = match.domain - match.start;
    // Without a scheme, nothing after "-", "_", "." or "/".
    if (!scheme && match.before >= 0 && '-_./'.includes(text[match.before]))
        return;
    // X's length test counts the scheme twice (and "https://" where there is none).
    if ((scheme || 'https://'.length) + match.end - match.start > MAX_URL_LENGTH)
        return;
    const domain = text.slice(match.domain, match.domainEnd);
    if (!fitsDns(domain))
        return;
    if (scheme) {
        const tco = TCO_LINK.exec(text.slice(match.start, match.end));
        if (tco && tco[1].length > MAX_TCO_SLUG)
            return;
        links.push({ start: match.start, end: tco ? match.start + tco[0].length : match.end });
        return;
    }
    const ascii = asciiDomains(domain);
    ascii.forEach(([offset, length], i) => {
        const start = match.domain + offset;
        const path = i === ascii.length - 1 && match.hasPath ? match.end - match.domainEnd : 0;
        links.push({ start, end: start + length + path });
    });
}
/**
 * The links X finds in `text` (already NFC), in order. Like X's pattern, the search takes the
 * leftmost place where a link can start (at the start of the text, or after a character that may
 * stand before one) and goes on after the whole match, whether X keeps the match or not.
 */
function xLinks(text) {
    const links = [];
    if (!text.includes('.'))
        return links;
    const domains = domainEnds(text);
    let from = 0;
    while (from < text.length) {
        let match = null;
        for (let at = from; at < text.length && !match; at += 1) {
            if (unitIs(text, at, PRECEDES_LINK))
                match = readLink(text, domains, at + 1, at);
            if (!match && at === 0)
                match = readLink(text, domains, 0, -1);
        }
        if (!match)
            break;
        linksInMatch(text, match, links);
        from = match.end;
    }
    return links;
}
//# sourceMappingURL=char-count.js.map